import { useCallback, useState } from 'react';
import { useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { useParams } from 'react-router';
import { Form, Input, Select, Button } from 'antd';
import type {
  PromptTemplate,
  PromptTemplateInput,
  PromptPipeline,
} from '@viq-admin/types';
import { PROMPT_PIPELINES, PIPELINE_LABELS, APPEND_BODY_MAX } from '../../api/promptStore';
import { MarkdownPromptEditor } from '../../components/MarkdownPromptEditor';
import { RequireCan } from '../../components/RequireCan';

/**
 * Edit a prompt template's append as a BARE form — no Refine `<Edit>` page
 * chrome — so it can render in place inside the shared `DetailPage`. Save
 * does NOT navigate (`redirect: false`); success is reported via `onSaved`
 * so `DetailPage` flips back to read mode. The form derives its own `dirty`
 * (useForm exposes none) and reports it via `onDirtyChange`.
 * `warnWhenUnsavedChanges: false` — `DetailPage` owns the discard guard.
 * Mirrors `ToolkitsEdit`'s bare-form pattern; simplified — templates have no
 * per-type field groups and no secrets. Differences from the toolkits form:
 *  - the `pipeline` select is DISABLED — immutable after create;
 *  - `pipeline` is RESENT on save (immutable, but the API body still needs
 *    it) — it is never itself editable.
 * The Delete button lives in `PromptTemplateDetail`'s read-mode header, not
 * here.
 *
 * All props are optional so a standalone edit test can render
 * `<PromptTemplatesEdit/>` bare. `id` is passed explicitly for robustness (at
 * /prompt-templates/:id the inferred Refine action is `show`, not `edit`); it
 * falls back to the route param.
 */

interface FormShape {
  name: string;
  pipeline?: PromptPipeline;
  body: string;
}

export interface PromptTemplatesEditProps {
  id?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function PromptTemplatesEdit({ id, onSaved, onDirtyChange }: PromptTemplatesEditProps = {}) {
  const params = useParams<{ id: string }>();
  const recordId = id ?? params.id;
  const [, setDirty] = useState(false);

  const { formProps, saveButtonProps, query, onFinish } = useForm<
    PromptTemplate,
    HttpError,
    PromptTemplateInput
  >({
    resource: 'prompt-templates',
    action: 'edit',
    id: recordId,
    redirect: false,
    warnWhenUnsavedChanges: false,
    onMutationSuccess: () => {
      setDirtyOnce(false);
      onSaved?.();
    },
  });

  const record = query?.data?.data;

  const setDirtyOnce = useCallback(
    (next: boolean) => {
      setDirty((prev) => {
        if (prev !== next) onDirtyChange?.(next);
        return next;
      });
    },
    [onDirtyChange],
  );

  function handleFinish(values: FormShape) {
    const body: PromptTemplateInput = {
      name: values.name,
      // pipeline is immutable; resend the record's value to satisfy the API
      // body shape without letting the user change it.
      pipeline: record?.pipeline as PromptPipeline,
      body: values.body,
    };
    return onFinish(body);
  }

  return (
    <RequireCan I="manage" a="PromptTemplate">
      <Form
        {...formProps}
        layout="vertical"
        initialValues={record}
        onValuesChange={() => setDirtyOnce(true)}
        onFinish={(values) => handleFinish(values as FormShape)}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input maxLength={200} />
        </Form.Item>

        <Form.Item label="Pipeline" name="pipeline" rules={[{ required: true }]}>
          <Select
            options={PROMPT_PIPELINES.map((p) => ({ label: PIPELINE_LABELS[p], value: p }))}
            disabled
          />
        </Form.Item>

        <Form.Item
          label="Append body"
          name="body"
          rules={[{ required: true, message: 'Body is required' }]}
        >
          <MarkdownPromptEditor max={APPEND_BODY_MAX} />
        </Form.Item>

        <Form.Item>
          {/*
            No htmlType="submit" here: saveButtonProps.onClick already calls
            form.submit() once. antd's Button defaults to htmlType="button",
            so a click drives exactly that one submit path — a submit button
            inside this <Form> would ALSO trigger the browser's native form
            submit, invoking rc-field-form's own onSubmit -> form.submit()
            a second time.
          */}
          <Button type="primary" {...saveButtonProps}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </RequireCan>
  );
}
